import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ChevronRight, 
  ChevronDown,
  Stethoscope,
  Heart,
  Droplets,
  Bug,
  User,
  Video,
  Circle,
  List,
  BookOpen,
  Baby
} from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import Breadcrumbs from '../components/Breadcrumbs';
import VideoCard from '../components/VideoCard';
import UnifiedSearch from '../components/UnifiedSearch';
import { getAllAnatomyPages } from '../data/anatomyPages';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category configuration matching user's master list
const categories = [
  { 
    id: 'kidney-stones', 
    name: 'Kidney Stones', 
    icon: Circle,
    description: 'Stone disease, prevention, and treatment',
    keywords: ['Kidney Stone', 'Stone Prevention', 'Lithotripsy', 'ESWL', 'PCNL', 'Ureteroscopy'],
    excludeKeywords: ['Bladder Stone']
  },
  { 
    id: 'kidney-upper', 
    name: 'Kidney & Upper Urinary Tract', 
    icon: Stethoscope,
    description: 'Hydronephrosis, renal cysts, small renal masses, adrenal masses, UPJ obstruction, bladder stones',
    keywords: ['Hydronephrosis', 'Renal Cyst', 'Renal Mass', 'Small Renal Mass', 'Adrenal', 'Bladder Stone', 'UPJ Obstruction', 'UPJ:'],
    excludeKeywords: ['Kidney Stone', 'Biopsy', 'Cancer', 'Nephrectomy', 'Ablative', 'Cryoablation']
  },
  { 
    id: 'prostate', 
    name: 'Prostate Conditions', 
    icon: User,
    description: 'BPH, prostatitis, chronic pelvic pain, elevated PSA',
    keywords: ['BPH', 'Benign Prostatic', 'Prostatitis', 'Elevated PSA', 'PSA:', 'CPPS', 'Pelvic Pain in Men', 'Chronic Prostatitis'],
    excludeKeywords: ['Cancer', 'Prostatectomy', 'Radiation', 'Active Surveillance']
  },
  { 
    id: 'bladder-voiding', 
    name: 'Bladder & Voiding Disorders', 
    icon: Droplets,
    description: 'OAB, urinary retention, interstitial cystitis, neurogenic bladder, urethral stricture',
    keywords: ['Overactive Bladder', 'OAB', 'Urinary Retention', 'Interstitial Cystitis', 'Painful Bladder', 'Neurogenic Bladder', 'Urethral Stricture', 'Weak Urinary Stream', 'Frequent Urination', 'Voiding'],
    excludeKeywords: ['Cancer', 'Incontinence', 'Stone']
  },
  { 
    id: 'incontinence', 
    name: 'Urinary Incontinence', 
    icon: Droplets,
    description: 'Stress, urge, and mixed incontinence',
    keywords: ['Incontinence', 'Leakage'],
    excludeKeywords: []
  },
  { 
    id: 'hematuria', 
    name: 'Hematuria', 
    icon: Droplets,
    description: 'Blood in the urine evaluation',
    keywords: ['Hematuria', 'Blood in the Urine', 'Blood in Urine'],
    excludeKeywords: []
  },
  { 
    id: 'infections', 
    name: 'Infections', 
    icon: Bug,
    description: 'UTI, recurrent UTIs, epididymitis, orchitis',
    keywords: ['UTI', 'Urinary Tract Infection', 'Epididymitis', 'Orchitis', 'Prevent UTI', 'Recurrent UTI'],
    excludeKeywords: []
  },
  { 
    id: 'mens-sexual', 
    name: 'Male Sexual Health', 
    icon: User,
    description: 'Erectile dysfunction, Peyronie\'s disease, low testosterone',
    keywords: ['Erectile Dysfunction', 'Peyronie', 'Low Testosterone', 'Hypogonadism', 'Testosterone Replacement'],
    excludeKeywords: ['Infertility', 'Varicocele']
  },
  { 
    id: 'mens-fertility', 
    name: 'Male Fertility & Reproductive', 
    icon: User,
    description: 'Male infertility, varicocele',
    keywords: ['Male Infertility', 'Infertility', 'Varicocele', 'Semen Analysis', 'Supplements for Male Infertility'],
    excludeKeywords: ['Post-Vasectomy']
  },
  { 
    id: 'scrotal', 
    name: 'Scrotal Conditions', 
    icon: User,
    description: 'Hydrocele, spermatocele, testicular pain, torsion',
    keywords: ['Hydrocele', 'Spermatocele', 'Testicular Pain', 'Testicular Torsion', 'Torsion', 'Scrotal'],
    excludeKeywords: ['Cancer', 'Orchiectomy']
  },
  { 
    id: 'external-genital', 
    name: 'External Genital Conditions', 
    icon: User,
    description: 'Phimosis, balanitis',
    keywords: ['Phimosis', 'Balanitis', 'Genital Wart', 'HPV'],
    excludeKeywords: ['Circumcision']
  },
  { 
    id: 'womens', 
    name: "Women's Pelvic Health", 
    icon: Heart,
    description: 'GSM, prolapse, pelvic floor',
    keywords: ['Menopause', 'GSM', 'Prolapse', 'Pelvic Organ', 'Genitourinary Syndrome'],
    categoryMatch: ['Womens Health', "Women's Health"],
    excludeKeywords: []
  },
  { 
    id: 'pediatric', 
    name: 'Pediatric Urology', 
    icon: Baby,
    description: 'Undescended testicle, hydrocele, hypospadias, VUR in children',
    keywords: ['Pediatric', 'Cryptorchidism', 'Hypospadias', 'Chordee', 'VUR', 'Vesicoureteral', 'Orchiopexy'],
    excludeKeywords: []
  }
];

// Articles to exclude (they belong in Procedures, Cancers, Tests, or After-Visit hubs)
const excludeKeywords = [
  'vs TURP', 'vs Aquablation', 'vs Radiation', 'Proton Beam', 'Photon Beam',
  'Active Surveillance', 'Prostate Cancer', 'Bladder Cancer', 'Testicular Cancer',
  'Muscle-Invasive', 'Treatment Options:', 'Radiation Therapy Side', 'Myths #',
  'Suprapubic Catheter', 'Circumcision', 'Semen Analysis', 'Renal Mass Biopsy'
];

const ConditionsPage = () => {
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [showAllExpanded, setShowAllExpanded] = useState(false);
  const [anatomyExpanded, setAnatomyExpanded] = useState(false);
  
  // Get anatomy pages
  const anatomyPages = getAllAnatomyPages();

  useEffect(() => {
    const loadContent = async () => {
      try {
        const [articlesRes, videosRes] = await Promise.all([
          axios.get(`${API}/blog?limit=100`),
          axios.get(`${API}/videos?limit=50`)
        ]);
        
        // Filter out articles that belong in other hubs
        const filtered = articlesRes.data.filter(article => {
          return !excludeKeywords.some(keyword => 
            article.title.includes(keyword)
          );
        });
        setArticles(filtered);
        
        // Filter videos for conditions (exclude procedure-heavy and cancer videos)
        const conditionVideos = videosRes.data.filter(v => {
          const title = v.title.toLowerCase();
          const tags = v.tags || [];
          return tags.some(t => ['bph', 'prostate', 'kidney', 'bladder', 'uti', 'ed', 'pelvic-floor', 'kegel'].includes(t.toLowerCase())) ||
                 title.includes('what is') || title.includes('understanding');
        });
        setVideos(conditionVideos.slice(0, 6));
      } catch (error) {
        console.error("Error loading content:", error);
      } finally {
        setLoading(false);
      }
    };
    loadContent();
  }, []);

  // Categorize articles with improved logic - CASE INSENSITIVE
  const categorizeArticle = (article) => {
    const title = (article.title || '').toLowerCase();
    const category = (article.category || '').toLowerCase();
    
    for (const cat of categories) {
      // Check category match first (for Women's Health)
      if (cat.categoryMatch && cat.categoryMatch.some(c => category.includes(c.toLowerCase()))) {
        if (cat.excludeKeywords && cat.excludeKeywords.some(ex => title.includes(ex.toLowerCase()))) {
          continue;
        }
        return cat.id;
      }
      // Then check keywords - case insensitive
      if (cat.keywords.some(keyword => title.includes(keyword.toLowerCase()))) {
        if (cat.excludeKeywords && cat.excludeKeywords.some(ex => title.includes(ex.toLowerCase()))) {
          continue;
        }
        return cat.id;
      }
    }
    return null;
  };

  const getArticlesByCategory = (categoryId) => {
    return articles.filter(article => categorizeArticle(article) === categoryId);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const allCategoryIds = categories
      .filter(cat => getArticlesByCategory(cat.id).length > 0)
      .map(cat => cat.id);
    setExpandedCategories(new Set(allCategoryIds));
    setShowAllExpanded(true);
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
    setShowAllExpanded(false);
  };

  // Sort articles alphabetically for the "Show All" section
  const sortedArticles = [...articles].sort((a, b) => a.title.localeCompare(b.title));

  return (
    <PageWrapper>
      <Breadcrumbs items={[
        { label: 'Home', path: '/' },
        { label: 'Conditions', path: '/conditions' }
      ]} />

      <div className="hub-header" data-testid="conditions-header">
        <Stethoscope size={48} className="hub-icon" />
        <h1>Condition Guides</h1>
        <p>Comprehensive information about urological conditions, causes, symptoms, and treatment options.</p>
      </div>

      {/* Search Bar */}
      <div className="hub-search-wrapper">
        <UnifiedSearch placeholder="Search conditions, symptoms, treatments..." variant="hub" />
      </div>

      {/* Expand/Collapse All Buttons */}
      <div className="accordion-controls" data-testid="accordion-controls">
        <button onClick={expandAll} className="accordion-control-btn">
          Expand All
        </button>
        <button onClick={collapseAll} className="accordion-control-btn">
          Collapse All
        </button>
      </div>

      {/* Accordion Category List */}
      {loading ? (
        <div className="loading-state">Loading conditions...</div>
      ) : (
        <div className="conditions-accordion" data-testid="conditions-accordion">
          {/* Show All Accordion - at the top */}
          <div className="accordion-section accordion-show-all" data-testid="accordion-show-all">
            <button
              className={`accordion-header ${showAllExpanded ? 'expanded' : ''}`}
              onClick={() => setShowAllExpanded(!showAllExpanded)}
              data-testid="accordion-header-show-all"
            >
              <div className="accordion-header-left">
                <List size={22} className="accordion-icon" />
                <div className="accordion-title-group">
                  <h3>Show All Articles</h3>
                  <span className="accordion-description">Browse all condition articles alphabetically</span>
                </div>
              </div>
              <div className="accordion-header-right">
                <span className="accordion-count">{articles.length}</span>
                {showAllExpanded ? (
                  <ChevronDown size={20} className="accordion-chevron" />
                ) : (
                  <ChevronRight size={20} className="accordion-chevron" />
                )}
              </div>
            </button>
            
            {showAllExpanded && (
              <div className="accordion-content accordion-content-all" data-testid="accordion-content-show-all">
                {sortedArticles.map(article => (
                  <Link 
                    to={`/conditions/${article.id}`}
                    key={article.id}
                    className="accordion-article-link"
                    data-testid={`article-all-${article.id}`}
                  >
                    <span className="article-title">{article.title}</span>
                    <ChevronRight size={16} className="article-arrow" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Category Accordions */}
          {categories.map(cat => {
            const IconComponent = cat.icon;
            const categoryArticles = getArticlesByCategory(cat.id);
            const count = categoryArticles.length;
            const isExpanded = expandedCategories.has(cat.id);
            
            if (count === 0) return null;
            
            return (
              <div key={cat.id} className="accordion-section" data-testid={`accordion-${cat.id}`}>
                <button
                  className={`accordion-header ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleCategory(cat.id)}
                  data-testid={`accordion-header-${cat.id}`}
                >
                  <div className="accordion-header-left">
                    <IconComponent size={22} className="accordion-icon" />
                    <div className="accordion-title-group">
                      <h3>{cat.name}</h3>
                      <span className="accordion-description">{cat.description}</span>
                    </div>
                  </div>
                  <div className="accordion-header-right">
                    <span className="accordion-count">{count}</span>
                    {isExpanded ? (
                      <ChevronDown size={20} className="accordion-chevron" />
                    ) : (
                      <ChevronRight size={20} className="accordion-chevron" />
                    )}
                  </div>
                </button>
                
                {isExpanded && (
                  <div className="accordion-content" data-testid={`accordion-content-${cat.id}`}>
                    {categoryArticles.map(article => (
                      <Link 
                        to={`/conditions/${article.id}`}
                        key={article.id}
                        className="accordion-article-link"
                        data-testid={`article-${article.id}`}
                      >
                        <span className="article-title">{article.title}</span>
                        <ChevronRight size={16} className="article-arrow" />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Anatomy Guides Accordion */}
          <div className="accordion-section accordion-anatomy" data-testid="accordion-anatomy">
            <button
              className={`accordion-header ${anatomyExpanded ? 'expanded' : ''}`}
              onClick={() => setAnatomyExpanded(!anatomyExpanded)}
              data-testid="accordion-header-anatomy"
            >
              <div className="accordion-header-left">
                <BookOpen size={22} className="accordion-icon" />
                <div className="accordion-title-group">
                  <h3>Anatomy Guides</h3>
                  <span className="accordion-description">Learn about parts of your urinary and reproductive system</span>
                </div>
              </div>
              <div className="accordion-header-right">
                <span className="accordion-count">{anatomyPages.length}</span>
                {anatomyExpanded ? (
                  <ChevronDown size={20} className="accordion-chevron" />
                ) : (
                  <ChevronRight size={20} className="accordion-chevron" />
                )}
              </div>
            </button>
            
            {anatomyExpanded && (
              <div className="accordion-content" data-testid="accordion-content-anatomy">
                {anatomyPages.map(anatomy => (
                  <Link 
                    to={`/anatomy/${anatomy.id}`}
                    key={anatomy.id}
                    className="accordion-article-link"
                    data-testid={`anatomy-${anatomy.id}`}
                  >
                    <span className="article-title">{anatomy.title}</span>
                    <span className="article-subtitle">{anatomy.subtitle}</span>
                    <ChevronRight size={16} className="article-arrow" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Related Videos Section */}
      {videos.length > 0 && (
        <section className="hub-videos-section" data-testid="conditions-videos">
          <div className="hub-videos-header">
            <h2><Video size={24} /> Educational Videos</h2>
            <Link to="/videos" className="view-all-link">
              View All Videos <ChevronRight size={16} />
            </Link>
          </div>
          <div className="hub-videos-grid">
            {videos.map(video => (
              <VideoCard key={video.id} video={video} compact={true} />
            ))}
          </div>
        </section>
      )}

      <div className="hub-disclaimer">
        <p>
          <strong>Medical Disclaimer:</strong> This information is for educational purposes only 
          and is not a substitute for professional medical advice. Always consult your physician.
        </p>
      </div>
    </PageWrapper>
  );
};

export default ConditionsPage;
